import React, { ChangeEvent, FC, useContext, useEffect, useState } from 'react';
import Section from '../../../components/Main/Section/Section';
import { useTranslation } from 'react-i18next';
import { CurrentViewContext } from '../../../providers/CurrentViewProvider';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  createFilterOptions,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import ActionBar from '../../../components/ActionBar/ActionBar';
import { DoNotDisturbAlt, RateReview } from '@mui/icons-material';
import Filter from '../../../components/Filter/Filter';
import { IGmbLocation } from '../../../shared/interfaces/IGmbLocation';
import GMBReview from '../../../components/GoogleMyBusiness/Review/GMBReview';
import GMBService from '../../../services/GMBService';
import { GMBAuthContext } from '../../../providers/GMBAuthProvider';
import Pagination from '../../../components/Pagination/Pagination';
import { IGMBReviewEntry } from '../../../shared/interfaces/IGMBReviewEntry';
import GoogleMyBusinessLogin from '../../../components/GoogleMyBusiness/GoogleMyBusinessLogin';
import Chip from '../../../components/Chip/Chip';
import { IGMBPaging } from '../../../shared/interfaces/IGMBPaging';
import LeftContainer from '../../../components/Containers/LeftContainer';
import RightContainer from '../../../components/Containers/RightContainer';
import SortContainer from '../../../components/Containers/SortContainer';
import ContentContainer from '../../../components/Containers/ContentContainer';
import SliderStarsAsLabels from '../../../components/Sliders/StarsAsLabels/SliderStarsAsLabels';
import DateRange from '../../../components/DateRange/DateRange';
import { format, isValid } from 'date-fns';
import { AuthContext } from '../../../providers/AuthProvider';

const defaultLocation = { title: '', id: '' };

const Reviews: FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { setCurrentView } = useContext(CurrentViewContext);
  const { user, login } = useContext(GMBAuthContext);
  const { hasRole } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState<IGmbLocation[]>([]);
  const [reviewEntries, setReviewEntries] = useState<IGMBReviewEntry[]>([]);

  // Filters & Pagination
  const [orderBy, setOrderBy] = useState<string[]>([]);
  const [location, setLocation] = useState<{ title: string; id: string }>(defaultLocation);
  const [score, setScore] = useState<number[]>([1, 5]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [filterByLocation, setFilterByLocation] = useState<IGmbLocation | null>(null);
  const [withComments, setWithComments] = useState<boolean>(true);
  const [withoutComments, setWithoutComments] = useState<boolean>(true);
  const [withDeletion, setWithDeletion] = useState<boolean>(false);
  const [withoutDeletion, setWithoutDeletion] = useState<boolean>(false);
  const [withDeleted, setWithDeleted] = useState<boolean>(false);
  const [withNote, setWithNote] = useState<boolean>(false);
  const [withoutNote, setWithoutNote] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(10);
  const [offset, setOffset] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [paging, setPaging] = useState<IGMBPaging>({
    pagesTotal: 0,
    totalItems: 0,
  });
  const [isUpdated, setIsUpdated] = useState<boolean>(false);
  const [currentUpdatedStatus, setCurrentUpdatedStatus] = useState<boolean>(false);

  // Triggers
  const [applyFilter, setApplyFilter] = useState<boolean>(false);

  // Errors
  const [noRecordsFound, setNoRecordsFound] = useState<boolean>(false);

  const prepareFilter = () => {
    setOffset(0);
    setPage(1);
    setApplyFilter(false);
  };

  const applyFilterWithReset = () => {
    setOffset(0);
    setPage(1);
    setApplyFilter(true);
  };

  const handleDeletionChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;

    setWithDeletion(isChecked);

    if (isChecked && !withoutDeletion) {
      setWithoutDeletion(true);
    } else {
      setWithoutDeletion((prevState) => !prevState);
    }
    setApplyFilter(false);
  };

  const handleNoteChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;

    setWithNote(isChecked);

    if (isChecked && !withoutNote) {
      setWithoutNote(true);
    } else {
      setWithoutNote((prevState) => !prevState);
    }
    setApplyFilter(false);
  };

  const handleOrderByChange = (event: SelectChangeEvent<typeof orderBy>) => {
    const {
      target: { value },
    } = event;
    setOrderBy(typeof value === 'string' ? value.split(',') : value);
    applyFilterWithReset();
  };

  const handleLocationFilterChange = (event: any, newValue: any) => {
    setFilterByLocation(newValue);
    setReviewEntries([]);
    setPaging((prevState) => ({
      ...prevState,
      pagesTotal: 0,
      totalItems: 0,
    }));
    setLocation({
      title: newValue?.locationData?.title || '',
      id: newValue?.locationID || '',
    });
    prepareFilter();
  };

  const handleScoreFilterChange = (event: Event, newValue: number | number[]) => {
    setScore(newValue as number[]);
    setApplyFilter(false);
  };

  const handleOnFromDateChange = (newValue: Date | null) => {
    setStartDate(newValue);
    setApplyFilter(false);
  };

  const handleOnToDateChange = (newValue: Date | null) => {
    setEndDate(newValue);
    setApplyFilter(false);
  };

  const handlePageNumberChange = (event: ChangeEvent<unknown>, pageNumber: number) => {
    setPage(pageNumber);
    setOffset(pageSize * pageNumber - pageSize);
    setApplyFilter(true);
  };

  const handlePageSizeChange = (event: SelectChangeEvent) => {
    setPageSize(parseInt(event.target.value));
    setPaging((prevState) => ({
      ...prevState,
      pagesTotal: 0,
      totalItems: 0,
    }));
    applyFilterWithReset();
  };

  useEffect(() => {
    setCurrentView(t('nav_reviews'));
    document.title = `Reputativ ${t('nav_reviews')}`;
  }, [setCurrentView, t]);

  useEffect(() => {
    if (!user) return;
    const controller = new AbortController();

    const getLocations = async (signal: AbortSignal) => {
      const data = await GMBService.getAllLocations(undefined, 1000, setLoading, signal, true);

      if (!data) return;
      setLocations(data.locations);
    };

    (async () => {
      await getLocations(controller.signal);
    })();

    return () => {
      controller.abort();
    };
  }, [user]);

  useEffect(() => {
    if (!applyFilter) return;

    const filter = async () => {
      if (!user) return;

      const data = await GMBService.getReviewsPaginated(
        {
          score,
          startDate: isValid(startDate) ? startDate : null,
          endDate: isValid(endDate) ? endDate : null,
          pageSize,
          offset,
          location: location.id || null,
          orderBy: orderBy.length === 0 ? null : orderBy,
          withComments,
          withoutComments,
          withHistory: false,
          withNote,
          withoutNote,
          withDeletion,
          withoutDeletion,
          isUpdated,
          withDeleted,
        },
        setLoading
      );

      if (!data.reviews) return;
      if (data.reviews.length === 0) {
        setNoRecordsFound(true);
      } else {
        setNoRecordsFound(false);
      }

      setReviewEntries(data.reviews);
      setPaging(data.paging);
    };
    (async () => await filter())();
  }, [
    applyFilter,
    withComments,
    withoutComments,
    endDate,
    location,
    offset,
    orderBy,
    page,
    pageSize,
    score,
    startDate,
    user,
    withNote,
    withoutNote,
    withDeletion,
    withoutDeletion,
    withDeleted,
  ]);

  const renderedLocationFilterName = (props: any, option: any, state: any, ownerState: any) => {
    const { key, ...optionProps } = props;
    const location: IGmbLocation = option;
    return (
      <Box
        key={key}
        component="li"
        {...optionProps}
      >
        <Box>
          <Typography>{location.locationData.title}</Typography>
          <Typography sx={{ fontSize: '10px' }}>
            {`(${location.locationData.address.addressLines}, ${location.locationData.address.postalCode} ${location.locationData.address.locality})`}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Section>
      <>
        {!user ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              borderRadius: `${theme.shape.borderRadius}px`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <GoogleMyBusinessLogin login={login} />
          </Box>
        ) : (
          <ContentContainer>
            <LeftContainer>
              <ActionBar>
                {/*<Checkbox
                        disabled
                        checked={multiActionChecked}
                        onChange={handleActionCheckboxChange}
                        sx={{mr: theme.spacing(1)}}
                    />
                      <Divider
                      orientation="vertical"
                      flexItem
                      />*/}
                {hasRole('can-use-note') ? (
                  <>
                    <IconButton
                      disabled
                      sx={{ mr: theme.spacing(1), ml: theme.spacing(1) }}
                    >
                      <RateReview />
                    </IconButton>
                  </>
                ) : null}
                {hasRole('can-use-deletion') ? (
                  <>
                    <IconButton disabled>
                      <DoNotDisturbAlt />
                    </IconButton>
                  </>
                ) : null}
              </ActionBar>
              <SortContainer>
                <Typography variant="body2">
                  {t('review_count')}: {paging.totalItems}
                </Typography>
                <FormControl
                  size="small"
                  sx={{ minWidth: '284px' }}
                >
                  <InputLabel id="sort-select-label">{t('sort')}</InputLabel>
                  <Select
                    labelId="sort-select-label"
                    value={orderBy}
                    input={<OutlinedInput label={t('sort')} />}
                    onChange={handleOrderByChange}
                    label={t('sort')}
                  >
                    <MenuItem value="score asc">{t('score_asc')}</MenuItem>
                    <MenuItem value="score desc">{t('score_desc')}</MenuItem>
                    <MenuItem value="date asc">{t('date_asc')}</MenuItem>
                    <MenuItem value="date desc">{t('date_desc')}</MenuItem>
                  </Select>
                </FormControl>
              </SortContainer>
              {loading ? (
                <Box>
                  <LinearProgress sx={{ my: theme.spacing(1) }} />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {!noRecordsFound &&
                    reviewEntries.length === 0 &&
                    getAlert(t('inactive_filter'), t('please_apply_filter'))}
                  {noRecordsFound ? (
                    getAlert(t('no_records'), t('no_records_found'))
                  ) : (
                    <>
                      {reviewEntries.map((reviewEntry) => {
                        const reviewLocation = locations.filter(
                          (f) => f.locationID === reviewEntry.company
                        )[0];
                        return (
                          <GMBReview
                            key={reviewEntry.review.reviewId}
                            rating={reviewEntry.review.rating}
                            comment={reviewEntry.review.comment}
                            createTime={reviewEntry.review.createTime}
                            name={reviewEntry.review.name}
                            reviewId={reviewEntry.review.reviewId}
                            postgresLocationId={reviewLocation.ID}
                            reviewReply={reviewEntry.review.reviewReply}
                            reviewer={reviewEntry.review.reviewer}
                            starRating={reviewEntry.review.starRating}
                            updateTime={reviewEntry.review.updateTime}
                            checked={false}
                            onChange={() => {}}
                            communityManagementSettings={reviewLocation.communityManagement}
                            note={reviewEntry.note}
                            deletion={reviewEntry.deletion}
                            id={reviewEntry.id}
                            isUpdated={isUpdated}
                            currentUpdatedStatus={reviewEntry.isUpdated}
                            deleted={reviewEntry.withDeleted}
                          />
                        );
                      })}
                    </>
                  )}
                </Box>
              )}
              <Pagination
                count={paging.pagesTotal}
                page={page}
                itemDescription={t('reviews')}
                showItemsPerPage={String(pageSize)}
                onPageChange={handlePageNumberChange}
                onItemsPerPageChange={handlePageSizeChange}
              />
            </LeftContainer>
            <RightContainer>
              <Filter
                disabled={loading}
                filters={
                  <>
                    <FormControl
                      disabled={loading}
                      size="small"
                      sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                    >
                      <Autocomplete
                        value={filterByLocation}
                        getOptionLabel={(l: any) => l.locationData.title}
                        getOptionKey={(l: any) => l.locationID}
                        options={locations}
                        filterOptions={createFilterOptions({
                          stringify: (option: any) =>
                            `${option.locationData.title} ${option.locationData.address.addressLines} ${option.locationData.address.postalCode} ${option.locationData.address.locality}`,
                        })}
                        onChange={handleLocationFilterChange}
                        renderOption={renderedLocationFilterName}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label={t('location')}
                          />
                        )}
                      />
                    </FormControl>
                    <Typography
                      variant="caption"
                      sx={{
                        textTransform: 'uppercase',
                        fontWeight: '700',
                        mt: theme.spacing(2),
                        color: loading ? theme.palette.text.disabled : theme.palette.text.primary,
                      }}
                    >
                      {t('score')}
                    </Typography>
                    <SliderStarsAsLabels
                      disabled={loading}
                      value={score}
                      onChange={handleScoreFilterChange}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        textTransform: 'uppercase',
                        fontWeight: '700',
                        mt: theme.spacing(2),
                        color: loading ? theme.palette.text.disabled : theme.palette.text.primary,
                      }}
                    >
                      {t('date')}
                    </Typography>
                    <DateRange
                      disabled={loading}
                      fromDate={startDate}
                      onFromDateChange={handleOnFromDateChange}
                      toDate={endDate}
                      onToDateChange={handleOnToDateChange}
                    />
                    <FormGroup sx={{ my: theme.spacing(1) }}>
                      <FormControlLabel
                        disabled={loading}
                        control={
                          <Switch
                            color={loading ? 'default' : 'primary'}
                            checked={withComments}
                            onChange={() => {
                              if (withComments && !withoutComments) {
                                setWithComments(true);
                                setWithoutComments(true);
                              } else {
                                setWithComments((prevState) => !prevState);
                              }
                              prepareFilter();
                            }}
                            inputProps={{ 'aria-label': 'controlled' }}
                          />
                        }
                        label={<Typography variant="body2">{t('reviews_with_comment')}</Typography>}
                      />
                      <FormControlLabel
                        disabled={loading}
                        control={
                          <Switch
                            color={loading ? 'default' : 'primary'}
                            checked={withoutComments}
                            onChange={() => {
                              if (withoutComments && !withComments) {
                                setWithComments(true);
                                setWithoutComments(true);
                              } else {
                                setWithoutComments((prevState) => !prevState);
                              }
                              prepareFilter();
                            }}
                            inputProps={{ 'aria-label': 'controlled' }}
                          />
                        }
                        label={
                          <Typography variant="body2">{t('reviews_without_comment')}</Typography>
                        }
                      />
                    </FormGroup>
                    {hasRole('can-use-deletion') ? (
                      <>
                        <FormGroup>
                          <FormControlLabel
                            disabled={loading}
                            control={
                              <Switch
                                checked={withDeletion}
                                onChange={handleDeletionChange}
                                inputProps={{ 'aria-label': 'controlled' }}
                              />
                            }
                            label={
                              <Typography variant="body2">{t('reviews_to_delete')}</Typography>
                            }
                          />
                        </FormGroup>
                      </>
                    ) : null}
                    {hasRole('can-use-note') ? (
                      <>
                        <FormGroup>
                          <FormControlLabel
                            disabled={loading}
                            control={
                              <Switch
                                checked={withoutNote}
                                onChange={handleNoteChange}
                                inputProps={{ 'aria-label': 'controlled' }}
                              />
                            }
                            label={
                              <Typography variant="body2">{'Bewertungen mit Notizen'}</Typography>
                            }
                          />
                        </FormGroup>
                      </>
                    ) : null}
                    {/*{ hasRole("can-use-deleted-reviews") ? (
                      <>
                      <FormGroup>
                      <FormControlLabel
                      disabled={loading}
                    control={
                      <Switch
                        checked={withDeleted}
                        onChange={() => {
                          setWithDeleted((prevState) => !prevState);
                          prepareFilter();
                        }}
                        inputProps={{ "aria-label": "controlled" }}
                      />
                    }
                    label={
                      <Typography variant="body2">{"Gelöschte Bewertungen"}</Typography>
                    }
                  />
                  </FormGroup>
                  </>
                  ) : null}*/}
                    <FormGroup>
                      <FormControlLabel
                        disabled={loading}
                        control={
                          <Switch
                            color={loading ? 'default' : 'primary'}
                            checked={isUpdated}
                            onChange={() => {
                              setIsUpdated((prevState) => !prevState);
                              prepareFilter();
                            }}
                            inputProps={{ 'aria-label': 'controlled' }}
                          />
                        }
                        label={<Typography variant="body2">{t('updated_ratings')}</Typography>}
                      />
                    </FormGroup>
                    <Divider sx={{ mt: theme.spacing(1) }} />
                    <Button
                      disabled={loading}
                      variant="contained"
                      onClick={() => {
                        setOffset(0);
                        setPage(1);
                        setApplyFilter(true);
                      }}
                      sx={{ mt: theme.spacing(1) }}
                    >
                      {t('apply')}
                    </Button>
                  </>
                }
                activeFilters={
                  <>
                    {location.title && applyFilter && (
                      <Chip
                        label={location.title}
                        onDelete={() => {
                          setReviewEntries([]);
                          setLocation(defaultLocation);
                          setPaging((prevState) => ({
                            ...prevState,
                            pagesTotal: 0,
                            totalItems: 0,
                          }));
                        }}
                      />
                    )}
                    {(score[0] !== 1 || score[1] !== 5) && applyFilter && (
                      <Chip
                        label={`${t('stars')} ${score[0]} - ${score[1]}`}
                        onDelete={() => {
                          setScore([1, 5]);
                          prepareFilter();
                        }}
                      />
                    )}
                    {startDate && isValid(startDate) && !endDate && applyFilter && (
                      <Chip
                        label={`${t('date_from')}: ${format(startDate, 'dd.MM.yyyy')}`}
                        onDelete={() => {
                          setStartDate(null);
                          prepareFilter();
                        }}
                      />
                    )}
                    {!startDate && endDate && isValid(endDate) && applyFilter && (
                      <Chip
                        label={`${t('date_to')}: ${format(endDate, 'dd.MM.yyyy')}`}
                        onDelete={() => {
                          setEndDate(null);
                          prepareFilter();
                        }}
                      />
                    )}
                    {startDate &&
                      isValid(startDate) &&
                      endDate &&
                      isValid(endDate) &&
                      applyFilter && (
                        <Chip
                          label={`${format(startDate, 'dd.MM.yyyy')} - ${format(
                            endDate,
                            'dd.MM.yyyy'
                          )}`}
                          onDelete={() => {
                            setStartDate(null);
                            setEndDate(null);
                            prepareFilter();
                          }}
                        />
                      )}
                    {withComments && !withoutComments && applyFilter && (
                      <Chip
                        label={t('reviews_with_comment')}
                        onDelete={() => {
                          setWithoutComments(true);
                          prepareFilter();
                        }}
                      />
                    )}
                    {withDeletion && withoutDeletion && applyFilter && (
                      <Chip
                        label={t('reviews_to_delete')}
                        onDelete={() => {
                          setWithDeletion(true);
                          prepareFilter();
                        }}
                      />
                    )}
                    {withNote && withoutNote && applyFilter && (
                      <Chip
                        label={'Bewertungen mit Notizen'}
                        onDelete={() => {
                          setWithNote(true);
                          prepareFilter();
                        }}
                      />
                    )}
                    {withoutComments && !withComments && applyFilter && (
                      <Chip
                        label={t('reviews_without_comment')}
                        onDelete={() => {
                          setWithComments(true);
                          prepareFilter();
                        }}
                      />
                    )}
                    {isUpdated && applyFilter && (
                      <Chip
                        label={'Aktualisiert Bewertungen'}
                        onDelete={() => {
                          setIsUpdated(false);
                          prepareFilter();
                        }}
                      />
                    )}
                    {withDeleted && applyFilter && (
                      <Chip
                        label={'Gelöschte Bewertungen'}
                        onDelete={() => {
                          setWithDeleted(false);
                          prepareFilter();
                        }}
                      />
                    )}
                    {withComments &&
                      withoutComments &&
                      !withDeletion &&
                      !withNote &&
                      applyFilter && <Chip label={t('all_reviews')} />}
                  </>
                }
                resetActiveFilters={() => {
                  setReviewEntries([]);
                  setLocation(defaultLocation);
                  setScore([1, 5]);
                  setStartDate(null);
                  setEndDate(null);
                  setWithComments(true);
                  setWithoutComments(true);
                  setWithNote(false);
                  setWithoutNote(false);
                  setWithDeletion(false);
                  setWithoutDeletion(false);
                  setWithDeleted(false);
                  setOffset(0);
                  setPage(1);
                  setPageSize(10);
                  setIsUpdated(false);
                  setPaging((prevState) => ({
                    ...prevState,
                    pagesTotal: 0,
                    totalItems: 0,
                  }));
                }}
              />
            </RightContainer>
          </ContentContainer>
        )}
      </>
    </Section>
  );

  function getAlert(title: string, message: string) {
    return (
      <Alert
        severity="warning"
        sx={{
          minWidth: '450px',
          maxWidth: '650px',
          alignSelf: 'center',
          my: theme.spacing(1),
        }}
      >
        <AlertTitle sx={{ fontWeight: '700' }}>{title}</AlertTitle>
        {message}
      </Alert>
    );
  }
};

export default Reviews;
